import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SiteSettings {
  id?: string;
  logo_url?: string;
  favicon_url?: string;
  site_name?: string;
  contact_email?: string;
  contact_phone?: string;
  hero_background_images?: string[];
  hero_overlay_opacity?: number;
  settings_version?: number;
}

export interface SettingsContextType {
  settings: SiteSettings;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  isOutdated: boolean;
}

export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const SETTINGS_CACHE_KEY = 'site_settings_cache';
const CACHE_VERSION_KEY = 'settings_cache_version';

interface SettingsProviderProps {
  children: ReactNode;
}

const loadFromCache = (): SiteSettings | null => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (error) {
    console.error('Error loading settings from cache:', error);
  }
  return null;
};

const saveToCache = (settings: SiteSettings) => {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
    if (settings.settings_version) {
      localStorage.setItem(CACHE_VERSION_KEY, settings.settings_version.toString());
    }
  } catch (error) {
    console.error('Error saving settings to cache:', error);
  }
};

const cachedSettings = loadFromCache();

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(cachedSettings || {});
  const [loading, setLoading] = useState(!cachedSettings);
  const [isOutdated, setIsOutdated] = useState(false);
  const { toast } = useToast();

  const fetchAndUpdate = async () => {
    try {
      const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);

      const { data: probe } = await supabase
        .from('site_settings')
        .select('settings_version')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const latestVersion = probe?.settings_version?.toString() ?? null;

      if (cachedSettings && cachedVersion && latestVersion && cachedVersion === latestVersion) {
        return;
      }

      const { data, error } = await supabase
        .from('site_settings')
        .select('id, logo_url, favicon_url, site_name, contact_email, contact_phone, hero_background_images, hero_overlay_opacity, settings_version')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const newSettings: SiteSettings = data || {};
      const isStale = cachedVersion !== newSettings.settings_version?.toString();

      if (isStale) {
        setSettings(newSettings);
        saveToCache(newSettings);
        if (cachedVersion) {
          setIsOutdated(true);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      if (!cachedSettings) {
        setSettings({});
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    setLoading(true);
    await fetchAndUpdate();
    setIsOutdated(false);
  };

  useEffect(() => {
    fetchAndUpdate();
  }, []);

  useEffect(() => {
    if (isOutdated) {
      toast({
        title: "Impostazioni aggiornate",
        description: "Le impostazioni del sito sono state aggiornate. Aggiorna la pagina per vedere le modifiche.",
        duration: 10000,
        action: (
          <button
            onClick={() => window.location.reload()}
            className="text-xs underline"
          >
            Aggiorna ora
          </button>
        ),
      });
    }
  }, [isOutdated, toast]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings, isOutdated }}>
      {children}
    </SettingsContext.Provider>
  );
};

export { useSettings } from '@/hooks/useSettings';
